import { Entity, Query, Dimension } from "../../api/OasisBackendApi";
import { PCAResult, PointWithData } from "../../utils/pca";

export interface Point2D {
    x: number;
    y: number;
    entity?: Entity;
    query?: Query;
    isSearchResult?: boolean;
    overlapCount?: number;
    overlapIndex?: number;
}

export interface TransformationConfig {
    mode: 'pca' | 'manual';
    pcaResult?: PCAResult;
    pcaSelectedDimensions?: Dimension[];
    selectedDimensions?: { x: Dimension | null; y: Dimension | null };
    useNormalized?: boolean;
}

/**
 * Extract dimension value from entity or query.
 * When useNormalized is true, uses the normalized embeddings used for actual search computation.
 */
function extractDimensionValue(
    point: { entity?: Entity; query?: Query },
    dimension: Dimension,
    useNormalized: boolean = false
): number | undefined {
    if (point.entity) {
        const metadata = point.entity.embedding?.metadata;
        const preferredKey = useNormalized ? 'normalized_entity_dimension_index' : 'entity_dimension_index';
        const fallbackKey = 'entity_dimension_index';
        const dimensionIndex = metadata?.[preferredKey] ?? metadata?.[fallbackKey];
        if (dimensionIndex) {
            return dimensionIndex[dimension.name] !== undefined
                ? dimensionIndex[dimension.name]
                : dimensionIndex[dimension.id];
        }
    } else if (point.query) {
        const preferred = useNormalized ? point.query.normalized_oasis_embedding : point.query.oasis_embedding;
        const oasisEmbedding = preferred ?? point.query.oasis_embedding;
        if (oasisEmbedding) {
            const val = oasisEmbedding[dimension.name] !== undefined
                ? oasisEmbedding[dimension.name]
                : oasisEmbedding[dimension.id];
            return val ?? undefined;
        }
    }
    return undefined;
}

/**
 * Build pointsWithData array in consistent order
 * This order must match the order used in computePCA
 */
export function buildPointsWithData(
    entities: Entity[],
    queries: Query[],
    searchResults: (Entity | Query)[]
): PointWithData[] {
    const pointsWithData: PointWithData[] = [];
    const searchResultIds = new Set(searchResults.map(r => r.id));
    
    // Add entities (same order as PCA computation)
    entities.forEach((entity) => {
        pointsWithData.push({
            entity,
            isSearchResult: searchResultIds.has(entity.id),
            originalIndex: pointsWithData.length
        });
    });
    
    // Add search results that are queries (if not in regular queries list)
    searchResults.forEach((searchResult) => {
        const isQuery = 'text' in searchResult;
        if (isQuery) {
            const query = searchResult as Query;
            if (!queries.some(q => q.id === query.id)) {
                pointsWithData.push({
                    query,
                    isSearchResult: true,
                    originalIndex: pointsWithData.length
                });
            }
        }
    });
    
    // Add regular queries
    queries.forEach((query) => {
        pointsWithData.push({
            query,
            isSearchResult: searchResultIds.has(query.id),
            originalIndex: pointsWithData.length
        });
    });
    
    // Add search results that are entities (if not in regular entities list)
    searchResults.forEach((searchResult) => {
        const isEntity = 'content' in searchResult;
        if (isEntity) {
            const entity = searchResult as Entity;
            if (!entities.some(e => e.id === entity.id)) {
                pointsWithData.push({
                    entity,
                    isSearchResult: true,
                    originalIndex: pointsWithData.length
                });
            }
        }
    });
    
    return pointsWithData;
}

/**
 * Transform high-dimensional data to 2D coordinates
 */
export function transformTo2D(
    entities: Entity[],
    queries: Query[],
    searchResults: (Entity | Query)[],
    config: TransformationConfig
): Point2D[] {
    if (config.mode === 'pca' && config.pcaResult) {
        // PCA mode: use transformed points from PCA result
        const pointsWithData = buildPointsWithData(entities, queries, searchResults);
        const points: Point2D[] = [];
        const searchResultIds = new Set(searchResults.map(r => r.id));
        let transformedIndex = 0;
        
        // Process entities in same order as PCA computation
        entities.forEach((entity) => {
            if (transformedIndex < config.pcaResult!.transformedPoints.length) {
                const transformedPoint = config.pcaResult!.transformedPoints[transformedIndex];
                const isSearchResult = searchResultIds.has(entity.id);
                points.push({
                    x: transformedPoint.x,
                    y: transformedPoint.y,
                    entity,
                    isSearchResult
                });
                transformedIndex++;
            }
        });
        
        // Process search results that are queries (if not in regular queries list)
        searchResults.forEach((searchResult) => {
            const isQuery = 'text' in searchResult;
            if (isQuery) {
                const query = searchResult as Query;
                if (!queries.some(q => q.id === query.id) && transformedIndex < config.pcaResult!.transformedPoints.length) {
                    const transformedPoint = config.pcaResult!.transformedPoints[transformedIndex];
                    points.push({
                        x: transformedPoint.x,
                        y: transformedPoint.y,
                        query,
                        isSearchResult: true
                    });
                    transformedIndex++;
                }
            }
        });
        
        // Process regular queries
        queries.forEach((query) => {
            if (transformedIndex < config.pcaResult!.transformedPoints.length) {
                const transformedPoint = config.pcaResult!.transformedPoints[transformedIndex];
                const isSearchResult = searchResultIds.has(query.id);
                points.push({
                    x: transformedPoint.x,
                    y: transformedPoint.y,
                    query,
                    isSearchResult
                });
                transformedIndex++;
            }
        });
        
        // Process search results that are entities (if not in regular entities list)
        searchResults.forEach((searchResult) => {
            const isEntity = 'content' in searchResult;
            if (isEntity) {
                const entity = searchResult as Entity;
                if (!entities.some(e => e.id === entity.id) && transformedIndex < config.pcaResult!.transformedPoints.length) {
                    const transformedPoint = config.pcaResult!.transformedPoints[transformedIndex];
                    points.push({
                        x: transformedPoint.x,
                        y: transformedPoint.y,
                        entity,
                        isSearchResult: true
                    });
                    transformedIndex++;
                }
            }
        });
        
        return points;
    } else if (config.mode === 'manual' && config.selectedDimensions) {
        // Manual mode: extract selected dimensions
        if (!config.selectedDimensions.x || !config.selectedDimensions.y) {
            return [];
        }
        
        const points: Point2D[] = [];
        const searchResultIds = new Set(searchResults.map(r => r.id));
        
        // Process entities
        entities.forEach(entity => {
            const xValue = extractDimensionValue({ entity }, config.selectedDimensions!.x!, config.useNormalized);
            const yValue = extractDimensionValue({ entity }, config.selectedDimensions!.y!, config.useNormalized);
            
            if (xValue !== undefined && yValue !== undefined) {
                const isSearchResult = searchResultIds.has(entity.id);
                points.push({
                    x: xValue,
                    y: yValue,
                    entity,
                    isSearchResult
                });
            }
        });
        
        // Process search results (these can be either entities or queries)
        searchResults.forEach((searchResult) => {
            const isQuery = 'text' in searchResult;
            const isEntity = 'content' in searchResult;
            
            if (isQuery) {
                const query = searchResult as Query;
                const xValue = extractDimensionValue({ query }, config.selectedDimensions!.x!, config.useNormalized);
                const yValue = extractDimensionValue({ query }, config.selectedDimensions!.y!, config.useNormalized);
                
                if (xValue !== undefined && yValue !== undefined) {
                    points.push({
                        x: xValue,
                        y: yValue,
                        query: query,
                        isSearchResult: true
                    });
                }
            } else if (isEntity) {
                const entity = searchResult as Entity;
                const xValue = extractDimensionValue({ entity }, config.selectedDimensions!.x!, config.useNormalized);
                const yValue = extractDimensionValue({ entity }, config.selectedDimensions!.y!, config.useNormalized);
                
                if (xValue !== undefined && yValue !== undefined) {
                    points.push({
                        x: xValue,
                        y: yValue,
                        entity: entity,
                        isSearchResult: true
                    });
                }
            }
        });
        
        // Process queries
        queries.forEach(query => {
            const xValue = extractDimensionValue({ query }, config.selectedDimensions!.x!, config.useNormalized);
            const yValue = extractDimensionValue({ query }, config.selectedDimensions!.y!, config.useNormalized);
            
            if (xValue !== undefined && yValue !== undefined) {
                const isSearchResult = searchResultIds.has(query.id);
                points.push({
                    x: xValue,
                    y: yValue,
                    query,
                    isSearchResult
                });
            }
        });
        
        return points;
    }
    
    return [];
}

/**
 * Transform specific entities/queries to 2D (for hulls, filtered views, etc.)
 */
export function transformFilteredTo2D(
    items: (Entity | Query)[],
    allEntities: Entity[],
    allQueries: Query[],
    allSearchResults: (Entity | Query)[],
    config: TransformationConfig
): Point2D[] {
    // Get all transformed points first
    const allPoints = transformTo2D(allEntities, allQueries, allSearchResults, config);
    
    // Create a map for quick lookup
    const pointMap = new Map<string, Point2D>();
    allPoints.forEach(point => {
        if (point.entity) {
            pointMap.set(`entity:${point.entity.id}`, point);
        } else if (point.query) {
            pointMap.set(`query:${point.query.id}`, point);
        }
    });
    
    // Filter to requested items
    const filteredPoints: Point2D[] = [];
    items.forEach(item => {
        const isEntity = 'content' in item;
        const key = isEntity ? `entity:${item.id}` : `query:${item.id}`;
        const point = pointMap.get(key);
        if (point) {
            filteredPoints.push(point);
        }
    });
    
    return filteredPoints;
}
